/**
 * FormControl Components
 * Form field wrapper with label, error message, and accessibility
 */

import React, { forwardRef, createContext, useContext, useId } from 'react';
import { Box } from '../primitives/Box';
import { Text } from '../primitives/Text';

/**
 * Form Control Context
 */
const FormControlContext = createContext({});

export const useFormControlContext = () => useContext(FormControlContext);

/**
 * FormControl Component
 * Provides context for form fields
 * 
 * @example
 * <FormControl isRequired isInvalid={hasError}>
 *   <FormLabel>Email</FormLabel>
 *   <Input type="email" />
 *   <FormErrorMessage>Email is required</FormErrorMessage>
 * </FormControl>
 */
export const FormControl = forwardRef(({
  isRequired = false,
  isDisabled = false,
  isInvalid = false,
  isReadOnly = false,
  id,
  children,
  ...props
}, ref) => {
  const generatedId = useId();
  const fieldId = id || `field-${generatedId}`;

  const context = {
    isRequired,
    isDisabled,
    isInvalid,
    isReadOnly,
    id: fieldId,
    labelId: `${fieldId}-label`,
    errorId: `${fieldId}-error`,
    helpTextId: `${fieldId}-helptext`,
  };

  return (
    <FormControlContext.Provider value={context}>
      <Box
        ref={ref}
        role="group"
        mb={4}
        {...props}
      >
        {children}
      </Box>
    </FormControlContext.Provider>
  );
});

FormControl.displayName = 'FormControl';

/**
 * FormLabel Component
 * Label for form fields
 */
export const FormLabel = forwardRef(({
  children,
  htmlFor,
  requiredIndicator = <RequiredIndicator />,
  optionalIndicator,
  ...props
}, ref) => {
  const context = useFormControlContext();

  return (
    <Text
      ref={ref}
      as="label"
      htmlFor={htmlFor || context.id}
      id={context.labelId}
      display="block"
      mb={1}
      fontSize="sm"
      fontWeight="medium"
      color="gray.700"
      transition="all 0.2s"
      opacity={context.isDisabled ? 0.4 : 1}
      {...props}
    >
      {children}
      {context.isRequired && requiredIndicator}
      {!context.isRequired && optionalIndicator}
    </Text>
  );
});

FormLabel.displayName = 'FormLabel';

/**
 * RequiredIndicator Component
 * Shows asterisk for required fields
 */
export const RequiredIndicator = forwardRef((props, ref) => (
  <Text
    ref={ref}
    as="span"
    role="presentation"
    aria-hidden="true"
    color="red.500"
    ml={1}
    {...props}
  >
    *
  </Text>
));

RequiredIndicator.displayName = 'RequiredIndicator';

/**
 * FormErrorMessage Component
 * Error message for invalid fields
 */
export const FormErrorMessage = forwardRef(({
  children,
  ...props
}, ref) => {
  const context = useFormControlContext();

  if (!context.isInvalid) return null;

  return (
    <Text
      ref={ref}
      id={context.errorId}
      color="red.500"
      fontSize="sm"
      mt={1}
      display="flex"
      alignItems="center"
      {...props}
    >
      {children}
    </Text>
  );
});

FormErrorMessage.displayName = 'FormErrorMessage';

/**
 * FormHelperText Component
 * Helper text for form fields
 */
export const FormHelperText = forwardRef(({
  children,
  ...props
}, ref) => {
  const context = useFormControlContext();

  return (
    <Text
      ref={ref}
      id={context.helpTextId}
      color="gray.500"
      fontSize="sm"
      mt={1}
      {...props}
    >
      {children}
    </Text>
  );
});

FormHelperText.displayName = 'FormHelperText';

export default FormControl;
